import type { GpuEffect, ItemEffect } from '@/types/effects'
import type { TimelineItem } from '@/types/timeline'

export const COLOR_GRADE_EFFECT_TYPES = ['gpu-color-wheels', 'gpu-curves'] as const

export type ColorGradeEffectType = (typeof COLOR_GRADE_EFFECT_TYPES)[number]

const COLOR_GRADE_EFFECT_TYPE_SET = new Set<string>(COLOR_GRADE_EFFECT_TYPES)

export interface ColorGradeSnapshotEntry {
  effect: GpuEffect
  enabled: boolean
}

export function isColorGradeEffectType(value: string): value is ColorGradeEffectType {
  return COLOR_GRADE_EFFECT_TYPE_SET.has(value)
}

export function isColorGradeEntry(entry: ItemEffect): entry is ItemEffect & { effect: GpuEffect } {
  return (
    entry.effect.type === 'gpu-effect' && isColorGradeEffectType(entry.effect.gpuEffectType)
  )
}

export function cloneGpuEffect(effect: GpuEffect): GpuEffect {
  return {
    ...effect,
    params: { ...effect.params },
  }
}

export function cloneColorGradeSnapshot(
  entries: readonly ColorGradeSnapshotEntry[],
): ColorGradeSnapshotEntry[] {
  return entries.map((entry) => ({
    effect: cloneGpuEffect(entry.effect),
    enabled: entry.enabled,
  }))
}

export function buildColorGradeSnapshot(item: TimelineItem): ColorGradeSnapshotEntry[] {
  return (item.effects ?? [])
    .filter(isColorGradeEntry)
    .map((entry) => ({
      effect: cloneGpuEffect(entry.effect),
      enabled: entry.enabled,
    }))
}

export function applyColorGradeSnapshotToEffects(
  currentEffects: readonly ItemEffect[] | undefined,
  snapshot: readonly ColorGradeSnapshotEntry[],
): ItemEffect[] {
  const preservedEffects = (currentEffects ?? []).filter((entry) => !isColorGradeEntry(entry))
  const nextGradeEffects = snapshot.map((entry) => ({
    id: crypto.randomUUID(),
    enabled: entry.enabled,
    effect: cloneGpuEffect(entry.effect),
  }))

  return [...preservedEffects, ...nextGradeEffects]
}
